import { ObjectLibraryTreeNode } from '@/model/GraphicModel';
import { DigiTracPermission, DigiTracPermissionItem, HardwarePermission } from '@/model/RolesModel';
import { Radio, Space } from 'antd';
import React from 'react';
import { getFullVelocityUrl } from '../../../../Helper';
import { HardwareType, ObjectLibraryFolder, ObjectTypes } from '../../../../model/DeviceAdminModel';
import { isDeepChildren } from './HardwarePermissions/SearchHardware/SearchHardware';

const imagesPath: string = `${getFullVelocityUrl()}Content/Images/Hardware/`;

const scriptVersion: string = `?_=${getScriptVersion()}`;
export const imagePaths = {
	[ObjectTypes.pollingPorts]: { imagePath: `${imagesPath}Port.svg${scriptVersion}`, imageName: _('Ports') },
	[ObjectTypes.xboxes]: { imagePath: `${imagesPath}Xbox.svg${scriptVersion}`, imageName: _('Xboxes') },
	[ObjectTypes.hardwareControllers]: { imagePath: `${imagesPath}Controller.svg${scriptVersion}`, imageName: _('Controller') },
	[ObjectTypes.doors]: { imagePath: `${imagesPath}Door-Closed.svg${scriptVersion}`, imageName: _('Doors') },
	[ObjectTypes.inputs]: { imagePath: `${imagesPath}Input.svg${scriptVersion}`, imageName: _('Inputs') },
	[ObjectTypes.relays]: { imagePath: `${imagesPath}Relay.svg${scriptVersion}`, imageName: _('Relays') },
	[ObjectTypes.readers]: { imagePath: `${imagesPath}Reader.svg${scriptVersion}`, imageName: _('Readers') },
	[ObjectTypes.expansionInputs]: { imagePath: `${imagesPath}ExpInput.svg${scriptVersion}`, imageName: _('ExpansionInputs') },
	[ObjectTypes.expansionRelays]: { imagePath: `${imagesPath}ExpRelay.svg${scriptVersion}`, imageName: _('ExpansionRelays') },
};

export const convertToObjectType = (hardwareType: HardwareType): ObjectTypes => {
	switch (hardwareType) {
		case HardwareType.Port:
			return ObjectTypes.pollingPorts;

		case HardwareType.Xbox:
			return ObjectTypes.xboxes;

		case HardwareType.Controller:
			return ObjectTypes.hardwareControllers;

		case HardwareType.Door:
			return ObjectTypes.doors;

		case HardwareType.Input:
			return ObjectTypes.inputs;

		case HardwareType.Relay:
			return ObjectTypes.relays;

		case HardwareType.Reader:
			return ObjectTypes.readers;

		case HardwareType.ExpansionInput:
			return ObjectTypes.expansionInputs;

		case HardwareType.ExpansionRelay:
			return ObjectTypes.expansionRelays;
	}
};

export const getDefaultDigiTracPermission = (): DigiTracPermission => {
	return {
		TotalSelectedItems: 0,
		ControllerDefinitions: [],
		ControllerFullSelected: [],
		Permissions: {
			[ObjectTypes.pollingPorts]: { Items: {} },
			[ObjectTypes.xboxes]: { Items: {} },
			[ObjectTypes.hardwareControllers]: { Items: {} },
			[ObjectTypes.doors]: { Items: {} },
			[ObjectTypes.expansionInputs]: { Items: {} },
			[ObjectTypes.expansionRelays]: { Items: {} },
			[ObjectTypes.inputs]: { Items: {} },
			[ObjectTypes.readers]: { Items: {} },
			[ObjectTypes.relays]: { Items: {} },
		} as unknown as Record<number, DigiTracPermissionItem>,
	};
};

export const buildRadioOptionsByEnum = <T extends {}>(direction: 'horizontal' | 'vertical', enumType: T, createParticiples: boolean = false) => {
	const options: string[] = Object.keys(enumType).filter(key => !isNaN(Number(enumType[key])) && key !== 'None');
	let ending = '';

	if (createParticiples) {
		ending = 'ing';
	}

	return (
		<Space direction={direction}>
			{options.map((enumKey: string) => (
				<Radio id={`radioOption-${enumKey}`} key={enumKey} value={enumType[enumKey]}>
					{`${_(enumKey)}${ending}`}
				</Radio>
			))}
		</Space>
	);
};

export const updateAllChildren = (node: ObjectLibraryTreeNode, isChecked: boolean, searchItems?: ObjectTypes[]): HardwarePermission[] => {
	let modifiedItems: HardwarePermission[] = [];
	if (!node) {
		return modifiedItems;
	}

	const shouldSkip = !(!searchItems?.length || (searchItems.length && searchItems.some(i => i === node.extendedData.objectType)));
	if (!shouldSkip) {
		const originalState = node.isChecked;
		node.isChecked = isChecked;
		if (node.folderType !== ObjectLibraryFolder.FOLDER && originalState !== isChecked) {
			let parentId = node?.parentNode?.extendedData?.objectId;
			if (isDeepChildren(node.extendedData.objectType, node) && node?.parentNode?.folderType === ObjectLibraryFolder.FOLDER) {
				parentId = node?.parentNode?.parentNode?.extendedData.objectId ?? 0;
			}

			modifiedItems.push({
				ObjectId: node.extendedData.objectId,
				ParentId: parentId,
				ObjectType: node.extendedData.objectType,
				HasPermission: isChecked,
			});
		}
	}

	if (!node?.children?.length) {
		return modifiedItems;
	}

	for (let i = 0; i < node.children.length; i++) {
		modifiedItems = modifiedItems.concat(updateAllChildren(node.children[i], isChecked, searchItems));
	}

	return modifiedItems;
};

/**
 *  Determines if all children are selected by checking children's allChildrenSelected property
 *  @param childNode node to look downwards
 */
export const isAllChildrenSelected = (childNode: ObjectLibraryTreeNode) => {
	if (childNode?.children?.length) {
		let allChildrenSelected: boolean = true;
		let isAnySelected: boolean = false;
		for (let i = 0; i < childNode.children.length; i++) {
			const c = childNode.children[i];

			let imChecked = true;
			if (c.folderType !== ObjectLibraryFolder.FOLDER) {
				imChecked = c.isChecked;
			}
			isAnySelected = isAnySelected || imChecked;
			//check if all my children of my child are checked
			const childAllChildrenSelected = isAllChildrenSelected(c);

			//check if my child is checked and all their children as well
			allChildrenSelected = allChildrenSelected && childAllChildrenSelected && imChecked;
		}
		let imChecked = true;
		if (childNode.folderType !== ObjectLibraryFolder.FOLDER) {
			imChecked = childNode.isChecked;
		}
		childNode.allChildrenSelected = allChildrenSelected;
		if (childNode.folderType === ObjectLibraryFolder.FOLDER) {
			childNode.isChecked = isAnySelected;
		}
		return allChildrenSelected && imChecked;
	}

	childNode.allChildrenSelected = true;
	return childNode.isChecked;
};

export const updateParentIsAllChildrenSelected = (currentNode: ObjectLibraryTreeNode) => {
	if (!currentNode) {
		return;
	}

	if (currentNode?.children?.length) {
		let isAnySelected: boolean = false;
		let allChildrenSelected: boolean = true;
		for (let i = 0; i < currentNode.children.length; i++) {
			const c = currentNode.children[i];

			let imChecked = true;
			if (c.folderType !== ObjectLibraryFolder.FOLDER) {
				imChecked = c.isChecked;
			}
			isAnySelected = isAnySelected || imChecked;

			//check if my child is checked and all their children as well
			allChildrenSelected = allChildrenSelected && c.allChildrenSelected && imChecked;
		}
		currentNode.allChildrenSelected = allChildrenSelected;

		if (currentNode.folderType === ObjectLibraryFolder.FOLDER && currentNode.extendedData.objectType !== ObjectTypes.servers) {
			currentNode.isChecked = isAnySelected;
		}
	} else {
		currentNode.allChildrenSelected = true;
	}

	updateParentIsAllChildrenSelected(currentNode?.parentNode);
};
